import { useLazyLoadQuery, useMutation } from "react-relay";
import graphql from "babel-plugin-relay/macro";
import styled from "styled-components";
import Button from "../components/Buttons/Button";
import Sidebar from "../components/Sidebar";
import { AccountQuery } from "./__generated__/AccountQuery.graphql";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { ReactComponent as CameraIcon } from "../assets/camera.svg";
import IconButton from "../components/Buttons/IconButton";
import { useImageUploader } from "../hooks/useImageUploader";
import { getWindowHeight } from "../utils";
import LogoutButton from "../components/Buttons/LogoutButton";

export function loader() {
  return null;
}

export function AccountContainer() {
  const fileInput = useRef<HTMLInputElement>(null);
  const data = useLazyLoadQuery<AccountQuery>(
    graphql`
      query AccountQuery {
        user {
          id
          email
          firstName
          lastName
          phoneNumber
          avatar {
            key @required(action: NONE)
            filename
            downloadUrl
            thumbnailUrl
            uploadedOn
          }
        }
      }
    `,
    {}
  );
  const [updateUserProfile, saving] = useMutation(
    graphql`
      mutation AccountModifyMutation(
        $firstName: String!
        $lastName: String!
        $email: String!
        $phoneNumber: String
      ) {
        updateUserProfile(
          input: {
            firstName: $firstName
            lastName: $lastName
            email: $email
            phoneNumber: $phoneNumber
          }
        ) {
          user {
            id
            firstName
            lastName
            email
            phoneNumber
            avatar {
              key @required(action: NONE)
              filename
              downloadUrl
              uploadedOn
            }
          }
        }
      }
    `
  );
  const { images, addImageList, deleteImage, uploadImages } = useImageUploader(
    data?.user?.avatar
      ? [
          {
            key: data?.user?.avatar?.key || "",
            downloadUrl: data?.user?.avatar?.downloadUrl || "",
            thumbnailUrl: data?.user?.avatar?.thumbnailUrl || "",
            name: data?.user?.avatar?.filename || "",
            timestamp:
              new Date(data?.user?.avatar?.uploadedOn || new Date()).getTime() /
              1000,
          },
        ]
      : []
  );
  const [updateAvatar] = useMutation(
    graphql`
      mutation AccountAvatarMutation($key: String) {
        updateAvatar(input: { storageobject: $key }) {
          user {
            id
            avatar {
              key @required(action: NONE)
              filename
              downloadUrl
              thumbnailUrl
              uploadedOn
            }
          }
        }
      }
    `
  );
  const [edit, setEdit] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      firstName: data?.user?.firstName,
      lastName: data?.user?.lastName,
      email: data?.user?.email,
      phoneNumber: data?.user?.phoneNumber,
    },
  });
  const handleSave = (formData: any) => {
    if (!Object.keys(errors)?.length) {
      updateUserProfile({
        variables: formData,
        onCompleted() {
          setEdit(false);
        },
      });
    }
  };
  useEffect(() => {
    const handleUpload = async () => {
      const avatars = await uploadImages();
      if (avatars?.length) {
        updateAvatar({
          variables: {
            key: avatars[0],
          },
        });
      }
    };
    if (images?.filter((i) => !i.downloadUrl)?.length) {
      handleUpload();
    }
  }, [updateAvatar, uploadImages, images]);
  return (
    <Container>
      <Sidebar />
      <Content>
        <Header>
          <Title>Mijn profiel</Title>
          <LogoutButton />
        </Header>
        <Body>
          <UserProfileImagePicker>
            <input
              ref={fileInput}
              type="file"
              accept="image/png, image/jpeg, image/jpg"
              style={{ display: "none" }}
              onChange={async ({ target }) => {
                if (!target?.files?.length) return;
                addImageList(target?.files, true);
              }}
            />
            {images?.length ? (
              <>
                <UserProfileImage
                  src={
                    images[0].path ||
                    images[0].thumbnailUrl ||
                    images[0].downloadUrl
                  }
                  alt={"Profielfoto"}
                  width={140}
                  height={140}
                  onClick={() => fileInput?.current?.click()}
                  style={{
                    cursor: "pointer",
                  }}
                />
                <FormButtons>
                  <IconButton
                    icon="pencil"
                    tooltip="Wijzig afbeelding"
                    onClick={() => fileInput?.current?.click()}
                    size="large"
                    iconColor="white"
                    backgroundColor="#0075BC"
                    borderColor="#0075BC"
                  />
                  <IconButton
                    icon="trash"
                    tooltip="Verwijder afbeelding"
                    onClick={() =>
                      updateAvatar({
                        variables: {},
                        onCompleted() {
                          deleteImage(data?.user?.avatar?.key);
                        },
                      })
                    }
                    size="large"
                  />
                </FormButtons>
              </>
            ) : (
              <>
                <ImagePlaceholder onClick={() => fileInput?.current?.click()}>
                  <CameraIcon width={25} height={25} fill="#0075BC" />
                </ImagePlaceholder>
                <IconButton
                  icon="upload"
                  tooltip="Upload afbeelding"
                  onClick={() => fileInput?.current?.click()}
                  size="large"
                  iconColor="white"
                  backgroundColor="#0075BC"
                  borderColor="#0075BC"
                />
              </>
            )}
          </UserProfileImagePicker>
          <UserProfileForm onSubmit={handleSubmit(handleSave)}>
            <InputTitle>Mijn gegevens</InputTitle>
            <FormGroupGrid>
              <InputGroup>
                <Label>Voornaam{edit ? ` *` : ""}</Label>
                {edit ? (
                  <>
                    <Input
                      placeholder="Voornaam"
                      {...register("firstName", {
                        required: true,
                      })}
                    />
                    {errors?.firstName ? (
                      <ErrorText>Dit veld is verplicht.</ErrorText>
                    ) : null}
                  </>
                ) : (
                  <Text>{data?.user?.firstName || "-"}</Text>
                )}
              </InputGroup>
              <InputGroup>
                <Label>Achternaam{edit ? ` *` : ""}</Label>
                {edit ? (
                  <>
                    <Input
                      placeholder="Achternaam"
                      {...register("lastName", {
                        required: true,
                      })}
                    />
                    {errors?.lastName ? (
                      <ErrorText>Dit veld is verplicht.</ErrorText>
                    ) : null}
                  </>
                ) : (
                  <Text>{data?.user?.lastName || "-"}</Text>
                )}
              </InputGroup>
              <InputGroup>
                <Label>Contact e-mailadres{edit ? ` *` : ""}</Label>
                {edit ? (
                  <>
                    <Input
                      placeholder="Contact e-mailadres"
                      {...register("email", {
                        required: true,
                        pattern: {
                          value: /\S+@\S+\.\S+/,
                          message: "Voer een valide emailadres in.",
                        },
                      })}
                    />
                    {errors?.email ? (
                      <ErrorText>
                        {errors?.email.type === "required"
                          ? "Dit veld is verplicht."
                          : errors?.email.message}
                      </ErrorText>
                    ) : null}
                  </>
                ) : (
                  <Text>{data?.user?.email || "-"}</Text>
                )}
              </InputGroup>
              <InputGroup>
                <Label>Telefoonnummer</Label>
                {edit ? (
                  <>
                    <Input
                      placeholder="Telefoonnummer"
                      {...register("phoneNumber")}
                    />
                  </>
                ) : (
                  <Text>{data?.user?.phoneNumber || "-"}</Text>
                )}
              </InputGroup>
            </FormGroupGrid>
            <FormButtons>
              {edit || saving ? (
                <>
                  <Button
                    key="submit_button"
                    type="submit"
                    color="green"
                    label="Opslaan"
                    disabled={saving}
                  />
                  <Button
                    key="cancel_button"
                    color="white"
                    bordered
                    label="Annuleren"
                    onClick={() => {
                      reset();
                      setEdit(false);
                    }}
                  />
                </>
              ) : (
                <Button
                  key="edit_button"
                  color="white"
                  bordered
                  label="Wijzigen"
                  onClick={() => setEdit(true)}
                />
              )}
            </FormButtons>
          </UserProfileForm>
        </Body>
      </Content>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: row;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  margin-top: 24px;
  margin-bottom: 43px;
`;

const Title = styled.h1`
  color: var(--donkergrijs, #212529);
  /* Tablet/H1 */
  font-size: 30px;
  font-family: Roboto;
  font-style: normal;
  font-weight: 700;
  line-height: 120%;
`;

const Content = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  height: ${getWindowHeight()};
  overflow: auto;
  padding: 0px 1rem 0px 1rem;
  @media only screen and (min-width: 768px) {
    padding: 0px 48px 0px 0px;
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: row;
  gap: 60px;
`;

const InputTitle = styled.span`
  color: var(--donkergrijs, #212529);
  /* Tablet/H3 */
  font-size: 18px;
  font-family: Roboto;
  font-style: normal;
  font-weight: 700;
  line-height: 120%;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
  justify-content: center;
  align-items: flex-start;
  padding-right: 0px;
  padding-bottom: 16px;
`;

const Label = styled.label`
  color: #212529;
  /* Text small */
  font-size: 13px;
  font-family: Roboto;
  font-style: normal;
  font-weight: 700;
  line-height: 120%;
`;

const Text = styled.span`
  display: flex;
  width: 258px;
  height: 50px;
  flex-direction: column;
  justify-content: center;
  color: var(--donkergrijs, #212529);
  /* Tablet/Text */
  font-size: 16px;
  font-family: Roboto;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
`;

const Input = styled.input`
  padding: 12px;

  color: var(--donkergrijs, #212529);
  /* Tablet/Text */
  font-size: 16px;
  font-family: Roboto;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  flex-shrink: 0;

  /* Donkergrijs */
  color: #212529;

  background: rgba(229, 229, 229, 1);

  /* Dropshadow */
  border-radius: 5px;
  border: 1px solid var(--mediumgrijs, #e5e5e5);
  background: #fff;
`;

const ErrorText = styled.div`
  color: #f92b2b;
  font-family: "Roboto";
  font-style: normal;
  font-weight: 700;
  font-size: 13px;
  line-height: 120%;

  /* or 19px */
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-top: 5px;
  padding-bottom: 5px;
`;

const UserProfileImagePicker = styled.label`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
`;

const UserProfileImage = styled.img`
  width: 140px;
  height: 140px;
  flex-shrink: 0;
  border-radius: 140px;
  object-fit: cover;
`;

const ImagePlaceholder = styled.div`
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 140px;
  height: 140px;
  flex-shrink: 0;
  border-radius: 140px;
  border: 1px dashed var(--boeren-natuur-blauw, #0075bc);
  background: rgba(0, 117, 188, 0.2);

  :hover {
    border: 1px solid var(--boeren-natuur-blauw, #0075bc);
    background: rgba(0, 117, 188, 0.2);
  }
`;

const UserProfileForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 25px;
`;
const FormGroupGrid = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: repeat(2, 400px);
  grid-template-rows: 80px;
  grid-row-end: 3;
  column-gap: 10px;
  row-gap: 15px;
`;
const FormButtons = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
`;

export default AccountContainer;
